import { useEffect } from 'react';

import FormTitle from 'components/PageLayout/FormTitle';
import { ButtonWithLoadingState } from 'components/curo-components/common/Button';
import Typography from 'components/curo-components/common/Typography';
import { GoogleAnalyticsEventType } from 'enums/GoogleAnalyticsEventType';
import { CuroApplicationVariables } from 'enums/curo/Variables';
import { getApplicationSelector } from 'handlers/curo-handlers/selectors';
import { useAppSelector } from 'hooks/reduxHooks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useGoogleAnalytics from 'hooks/useGoogleAnalytics';
import {
  BranchOfferFlowSteps,
  BranchOfferType,
} from 'pages/CuroPages/ApplicationProcess/steps/GenerateLoanOffersStep/constants';
import formatDate from 'product_modules/utils/dateFormat';
import { runApplicationSummitLead, updatePageDisplayedTechnical } from 'thunks';

import styles from './SelectBranchOfferStep.module.scss';

interface ISelectBranchOfferStepProps {
  onStepComplete: (step: BranchOfferFlowSteps) => void;
}

const SelectBranchOfferStep = ({ onStepComplete }: ISelectBranchOfferStepProps) => {
  const application = useAppSelector(getApplicationSelector)!;
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const trackEvent = useGoogleAnalytics();

  const offerExpirationDate = application.variables[
    CuroApplicationVariables.OfferExpirationDate
  ] as string;
  const offerPageText1 = application.variables[CuroApplicationVariables.OrganicOfferPageTextOne];
  const offerPageText2 = application.variables[CuroApplicationVariables.OrganicOfferPageTextTwo];
  const branchOffer = !application.variables[CuroApplicationVariables.BranchType]
    ? BranchOfferType.Physical
    : application.variables[CuroApplicationVariables.BranchType];
  const isPlaidIntegrationsCompleted =
    application.variables[CuroApplicationVariables.PlaidAuthAndIdentityIntegrationsPassed] &&
    application.variables[CuroApplicationVariables.PlaidCraIntegrationsPassed];

  useEffect(() => {
    trackEvent(GoogleAnalyticsEventType.ApplyProcessBranchOffer);
  }, []);

  const handleContinue = async () => {
    await dispatchWithUnwrap(
      updatePageDisplayedTechnical({
        applicationId: application?.id,
        pageVariableToUpdate: CuroApplicationVariables.BranchOfferInteractionTechnical,
      }),
    );

    if (branchOffer === BranchOfferType.Physical) {
      dispatchWithUnwrap(runApplicationSummitLead({ applicationId: application.id }));
      onStepComplete(BranchOfferFlowSteps.PhysicalBranchOfferStep);
    } else if (branchOffer === BranchOfferType.Central && !isPlaidIntegrationsCompleted) {
      onStepComplete(BranchOfferFlowSteps.ConnectBankAccountForFundingStep);
    } else if (branchOffer === BranchOfferType.Central && isPlaidIntegrationsCompleted) {
      dispatchWithUnwrap(runApplicationSummitLead({ applicationId: application.id }));
      onStepComplete(BranchOfferFlowSteps.CentralBranchOfferStep);
    }
  };

  const renderContent = () => {
    return (
      <div className={styles.contentWrapper}>
        <Typography className={styles.regularText}>
          Based on information provided, we can offer you the following loan options:
        </Typography>

        <div className={styles.loanBox}>
          <Typography className={styles.loanBoxText}>{offerPageText1}</Typography>
          {offerPageText2 && (
            <>
              Or
              <Typography className={styles.loanBoxText}>{offerPageText2}</Typography>
            </>
          )}
          <Typography className={styles.smallLoanBoxText}>
            This offer expires on {formatDate(offerExpirationDate)}
          </Typography>
        </div>

        <Typography className={styles.regularText}>
          Final approval is conditional based on verification of identity, address, monthly income
          and expenses - additionally, for vehicle secured loans, collateral value.
        </Typography>

        <ButtonWithLoadingState kind="primary" size="form" onClick={handleContinue}>
          Next Steps
        </ButtonWithLoadingState>
      </div>
    );
  };

  return (
    <>
      <FormTitle title="You're pre-qualified for a loan from Heights Finance!" />
      {renderContent()}
    </>
  );
};

export default SelectBranchOfferStep;
